import { AlertTriangle } from 'lucide-react';
import { colors } from '../../styles/colors';
import { normalizeDate } from '../../utils/formatters';
import { DatePicker } from '../ui/DatePicker';

function ReceiptDataForm({
  data,
  isEditing,
  editedData,
  validationErrors,
  onFieldChange
}) {
  const styles = {
    extractedDataSection: {
      marginBottom: '1.5rem',
    },
    subsectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: colors.text.dark,
      marginBottom: '1rem',
      marginTop: 0,
    },
    dataGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem',
    },
    dataItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    dataLabel: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: colors.text.secondary,
    },
    dataInput: {
      padding: '0.75rem',
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      fontSize: '1rem',
      color: colors.text.dark,
    },
    dataInputReadOnly: {
      borderColor: colors.border.light,
      backgroundColor: colors.background.light,
    },
    dataInputEditable: {
      backgroundColor: colors.background.white,
      borderColor: colors.primary.teal,
      cursor: 'text'
    },
    dataInputError: {
      borderColor: colors.status.error,
      backgroundColor: '#FFF5F5'
    },
    fullTextSection: {
      marginTop: '1.5rem',
    },
    textArea: {
      width: '100%',
      padding: '1rem',
      borderRadius: '6px',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: colors.border.light,
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      backgroundColor: colors.background.light,
      color: colors.text.dark,
      resize: 'vertical',
    },
    confidence: {
      fontSize: '0.875rem',
      fontWeight: '400',
      color: colors.text.secondary,
      marginLeft: '0.5rem',
    },
  };

  return (
    <>
      {/* Erreurs validation */}
      {Object.keys(validationErrors).length > 0 && (
        <div style={{
          backgroundColor: colors.background.lightRed,
          border: `1px solid ${colors.status.error}`,
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <AlertTriangle size={18} color={colors.status.error} />
            <strong style={{ color: colors.text.darkRed }}>Erreurs de validation:</strong>
          </div>
          <ul style={{ margin: '0.5rem 0 0 1.5rem', color: colors.text.darkRed }}>
            {Object.entries(validationErrors).map(([field, error]) => (
              <li key={field}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Données extraites */}
      <div style={styles.extractedDataSection}>
        <h4 style={styles.subsectionTitle}>Données extraites</h4>
        <div style={styles.dataGrid}>
          <div style={styles.dataItem}>
            <label style={styles.dataLabel}>Nom du document</label>
            <input
              type="text"
              value={isEditing ? editedData.nom : data.nom || ''}
              onChange={(e) => onFieldChange('nom', e.target.value)}
              readOnly={!isEditing}
              style={{
                ...styles.dataInput,
                ...(isEditing ? styles.dataInputEditable : styles.dataInputReadOnly)
              }}
            />
          </div>
          <div style={styles.dataItem}>
            <label style={styles.dataLabel}>Fournisseur *</label>
            <input
              type="text"
              value={isEditing ? editedData.fournisseur : data.fournisseur || ''}
              onChange={(e) => onFieldChange('fournisseur', e.target.value)}
              readOnly={!isEditing}
              style={{
                ...styles.dataInput,
                ...(validationErrors.fournisseur ? styles.dataInputError :
                    (isEditing ? styles.dataInputEditable : styles.dataInputReadOnly))
              }}
            />
          </div>
          <div style={styles.dataItem}>
            <label style={styles.dataLabel}>Date *</label>
            <DatePicker
              value={editedData.date ? normalizeDate(editedData.date) : (data.date ? normalizeDate(data.date) : '')}
              onChange={(v) => onFieldChange('date', v)}
              readOnly={!isEditing}
              style={{
                ...styles.dataInput,
                ...(validationErrors.date ? styles.dataInputError :
                    (isEditing ? styles.dataInputEditable : styles.dataInputReadOnly))
              }}
            />
          </div>
          <div style={styles.dataItem}>
            <label style={styles.dataLabel}>Montant HT</label>
            <input
              type={isEditing ? "number" : "text"}
              step="0.01"
              value={
                isEditing
                  ? editedData.montantHT
                  : (data.montantHT ? `${data.montantHT} €` : '')
              }
              onChange={(e) => onFieldChange('montantHT', e.target.value)}
              readOnly={!isEditing}
              style={{
                ...styles.dataInput,
                ...(validationErrors.montantHT ? styles.dataInputError :
                    (isEditing ? styles.dataInputEditable : styles.dataInputReadOnly))
              }}
            />
          </div>
          <div style={styles.dataItem}>
            <label style={styles.dataLabel}>
              TVA ({isEditing ? editedData.tauxTVA : data.tauxTVA || 'N/A'}%)
            </label>
            <input
              type={isEditing ? "number" : "text"}
              step="0.01"
              value={
                isEditing
                  ? editedData.montantTVA
                  : (data.montantTVA ? `${data.montantTVA} €` : '')
              }
              onChange={(e) => onFieldChange('montantTVA', e.target.value)}
              readOnly={!isEditing}
              style={{
                ...styles.dataInput,
                ...(validationErrors.montantTVA ? styles.dataInputError :
                    (isEditing ? styles.dataInputEditable : styles.dataInputReadOnly))
              }}
            />
          </div>
          <div style={styles.dataItem}>
            <label style={styles.dataLabel}>Montant TTC *</label>
            <input
              type={isEditing ? "number" : "text"}
              step="0.01"
              value={
                isEditing
                  ? editedData.montantTTC
                  : (data.montantTTC ? `${data.montantTTC} €` : '')
              }
              onChange={(e) => onFieldChange('montantTTC', e.target.value)}
              readOnly={!isEditing}
              style={{
                ...styles.dataInput,
                ...(validationErrors.montantTTC ? styles.dataInputError :
                    (isEditing ? styles.dataInputEditable : styles.dataInputReadOnly)),
                fontWeight: '600',
                color: colors.primary.teal
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default ReceiptDataForm;
